import { useCallback, useEffect, useRef, useState } from "react";
import { Send, StopCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { agentChat, type AgentStatusResponse } from "@app/lib/api";
import { readChatStream } from "@app/lib/sse";
import { cn } from "@app/lib/utils";

interface ChatProps {
  agent: AgentStatusResponse | null;
}

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ErrorState {
  code: string;
  message: string;
}

// User-facing copy for the documented error codes returned by
// functions/api/agent/chat.ts. Anything not in this map renders the raw
// message from the proxy.
const ERROR_COPY: Record<string, string> = {
  csrf_failed: "Your session is out of sync. Reload the page and try again.",
  unauthenticated: "Your session expired. Sign in again.",
  no_agent: "No agent is provisioned for your account yet.",
  not_running: "Your agent is not running. Start it from the Agent screen.",
  ipv4_lookup_failed: "Could not reach the Hetzner API to find your VM's address.",
  no_ipv4: "Your agent VM does not have a public IPv4 address.",
  upstream_unreachable: "Your agent did not respond. It may be restarting.",
  gateway_token_missing: "Your agent's gateway token is missing. Rebuild the agent from the Agent screen.",
};

function copyForCode(code: string, fallback: string): string {
  return ERROR_COPY[code] ?? fallback;
}

function emptyStateCopy(agent: AgentStatusResponse | null): string {
  if (!agent || agent.status === "none") {
    return "No agent is provisioned yet. Provision one from the Agent screen.";
  }
  switch (agent.status) {
    case "provision_requested":
    case "creating":
    case "awaiting_phone_home":
      return "Your agent is provisioning. This usually takes 60-90 seconds.";
    case "stopped":
      return "Your agent is stopped. Start it from the Agent screen.";
    case "error":
      return "Your agent is in an error state. Check the Agent screen for details.";
    default:
      return "Your agent is not ready for chat yet.";
  }
}

export default function Chat({ agent }: ChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const pinToBottomRef = useRef(true);

  const isReady = agent?.status === "running";
  const canSend = isReady && !pending && input.trim().length > 0;

  // Track whether the user is already near the bottom; only auto-scroll if so.
  const onScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinToBottomRef.current = distance < 80;
  }, []);

  useEffect(() => {
    if (!pinToBottomRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPending(false);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || !isReady || pending) return;
    setError(null);
    setInput("");
    pinToBottomRef.current = true;

    const nextMessages: Message[] = [
      ...messages,
      { role: "user", content: text },
      { role: "assistant", content: "" },
    ];
    setMessages(nextMessages);
    setPending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await agentChat(
        {
          model: "chat",
          stream: true,
          messages: nextMessages
            .slice(0, -1)
            .map(({ role, content }) => ({ role, content })),
        },
        { signal: controller.signal },
      );

      if (!res.ok) {
        let code = "http_error";
        let msg = `Upstream returned ${res.status}.`;
        try {
          const body = (await res.clone().json()) as { code?: string; message?: string };
          if (typeof body?.code === "string") code = body.code;
          if (typeof body?.message === "string") msg = body.message;
        } catch {
          // ignore - keep defaults
        }
        setError({ code, message: copyForCode(code, msg) });
        // Drop the empty assistant placeholder.
        setMessages((m) => m.slice(0, -1));
        return;
      }

      if (!res.body) {
        setError({ code: "no_body", message: "Upstream sent no response body." });
        setMessages((m) => m.slice(0, -1));
        return;
      }

      const reader = res.body.getReader();
      await readChatStream(reader, (delta) => {
        setMessages((m) => {
          if (m.length === 0) return m;
          const last = m[m.length - 1];
          if (last.role !== "assistant") return m;
          const updated: Message = { ...last, content: last.content + delta };
          return [...m.slice(0, -1), updated];
        });
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") {
        // User-cancelled; keep whatever was streamed so far.
        return;
      }
      const msg = e instanceof Error ? e.message : String(e);
      setError({ code: "network_error", message: msg });
      setMessages((m) => m.slice(0, -1));
    } finally {
      abortRef.current = null;
      setPending(false);
    }
  }, [input, isReady, pending, messages]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col gap-4">
      <Card className="flex min-h-0 flex-1 flex-col">
        <CardHeader>
          <CardTitle>Chat</CardTitle>
          <CardDescription>
            Conversation runs through the control plane to your VM. Messages are
            not persisted - reloading clears the thread.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col gap-3 p-0">
          <div
            ref={scrollerRef}
            onScroll={onScroll}
            className="min-h-0 flex-1 overflow-y-auto px-6 py-2"
          >
            {messages.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-fg">
                {isReady ? "Say hello to your agent." : emptyStateCopy(agent)}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm",
                      m.role === "user"
                        ? "border-accent/30 bg-accent/10 text-fg"
                        : "border-border bg-muted/40 text-fg",
                    )}
                  >
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-fg">
                      {m.role === "user" ? "You" : "Agent"}
                    </div>
                    <div className="whitespace-pre-wrap break-words">
                      {m.content || (m.role === "assistant" && pending ? "..." : "")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error ? (
            <div className="mx-6 rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error.message}
            </div>
          ) : null}

          <div className="border-t border-border bg-muted/20 px-6 py-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={!isReady}
                rows={2}
                placeholder={isReady ? "Type a message. Enter to send, Shift+Enter for newline." : emptyStateCopy(agent)}
                className={cn(
                  "flex-1 resize-none rounded-lg border border-border bg-bg px-3 py-2 text-sm placeholder:text-muted-fg",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50",
                )}
              />
              {pending ? (
                <Button type="button" variant="outline" onClick={cancel}>
                  <StopCircle className="h-4 w-4" aria-hidden />
                  Stop
                </Button>
              ) : (
                <Button type="button" onClick={() => void send()} disabled={!canSend}>
                  <Send className="h-4 w-4" aria-hidden />
                  Send
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
